import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Check, X, Clock, Quote } from 'lucide-react';
import { toast } from 'sonner';
import { formatRelativeTime } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Skeleton } from '@/client/components/ui/skeleton';

interface AccessRequest {
  id: string;
  name: string;
  email: string;
  reason?: string;
  createdAt: string;
}

export function AdminAccessRequestsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'access-requests'],
    queryFn: async () => {
      const response = await apiFetch('/api/admin/access-requests?status=pending');
      if (!response.ok) throw new Error('Failed to fetch requests');
      return response.json();
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const response = await apiFetch(`/api/admin/access-requests/${requestId}/approve`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to approve request');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'access-requests'] });
      toast.success('Request approved! User account created.');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const response = await apiFetch(`/api/admin/access-requests/${requestId}/reject`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to reject request');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'access-requests'] });
      toast.success('Request rejected');
    },
  });

  return (
    <div className="space-y-6 stagger-children">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Access Requests</h1>
        <p className="text-muted-foreground mt-1">Review and approve beta access requests.</p>
      </div>

      <Card className="rounded-2xl py-0">
        {isLoading ? (
          <CardContent className="p-4 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-4">
                <Skeleton className="size-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </CardContent>
        ) : data?.requests?.length === 0 ? (
          <CardContent className="p-12 text-center">
            <div className="size-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <UserPlus className="size-8 text-muted-foreground/50" />
            </div>
            <h3 className="font-semibold mb-1">All caught up!</h3>
            <p className="text-muted-foreground">No pending access requests.</p>
          </CardContent>
        ) : (
          <div className="divide-y">
            {data?.requests?.map((request: AccessRequest) => (
              <div key={request.id} className="p-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-4 flex-1">
                    <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <UserPlus className="size-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{request.name}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground truncate">{request.email}</span>
                      </div>
                      {request.reason && (
                        <div className="flex items-start gap-2 mt-2 p-3 rounded-xl bg-muted/50">
                          <Quote className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                          <p className="text-sm text-muted-foreground italic">{request.reason}</p>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Clock className="size-3" />
                        Requested {formatRelativeTime(request.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => approveMutation.mutate(request.id)}
                      disabled={approveMutation.isPending}
                      className="rounded-xl"
                    >
                      <Check className="size-4" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => rejectMutation.mutate(request.id)}
                      disabled={rejectMutation.isPending}
                      className="rounded-xl"
                    >
                      <X className="size-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
